import { FC, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/slice';
import { ftuxSelectors, FtuxSlice } from '../../store/slice/Ftux';
import Joyride, { Step, CallBackProps, ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { FtuxConfig, FtuxStep } from './types';
import FtuxModal from './FtuxModal';
import FtuxTooltip from './FtuxTooltip';
import apibridge from '../../apibridge';
import { TutorialType } from '../../api/models';
import { UserSlice } from '../../store/slice/User';

interface DynamicFtuxProps {
	config: FtuxConfig;
	tutorialType?: TutorialType;
	manualRestartTrigger?: number; // When this changes, manually restart the tutorial (even if dismissed)
}

const isTooltipTargetAvailable = (step: FtuxStep | undefined) => {
	if (typeof document === 'undefined') {
		return true;
	}

	if (!step?.target) {
		return false;
	}

	return Boolean(document.querySelector(step.target));
};

// Async function that finds the next available step starting from a given index
// Waits briefly for elements to appear (for dynamic content)
// Returns the index of the first available step, or null if none found
const findNextAvailableStepAsync = async (
	steps: FtuxStep[],
	startIndex: number,
	waitTimeout: number = 500
): Promise<number | null> => {
	for (let i = startIndex; i < steps.length; i++) {
		const step = steps[i];

		// Modal steps are always available (they don't require a DOM target)
		if (step.type === 'modal') {
			return i;
		}

		// For tooltip steps, check if target exists
		if (step.type === 'tooltip') {
			if (!step.target) {
				// Step has no target requirement, so it's available
				return i;
			}

			// Wait briefly for the target to appear (for dynamic content)
			const element = await waitForElement(step.target, waitTimeout, 50);
			if (element) {
				return i;
			}
			// If target doesn't exist after waiting, continue to next step
		}
	}

	// No available step found
	return null;
};

// Wait for an element to appear with timeout and retry logic
const waitForElement = (selector: string, timeout: number = 5000, interval: number = 100): Promise<Element | null> => {
	return new Promise((resolve) => {
		const startTime = Date.now();

		const checkElement = () => {
			const element = document.querySelector(selector);
			if (element) {
				resolve(element);
				return;
			}

			if (Date.now() - startTime >= timeout) {
				console.warn(`[FTUX] Target element "${selector}" not found after ${timeout}ms`);
				resolve(null);
				return;
			}

			setTimeout(checkElement, interval);
		};

		checkElement();
	});
};

const DynamicFtux: FC<DynamicFtuxProps> = ({ config, tutorialType, manualRestartTrigger }) => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	// Get tutorial dismissal and onboarding status from Redux
	const onboardingCompleted = useAppSelector(ftuxSelectors.isOnboardingCompleted);
	const isTutorialDismissed = useAppSelector(
		tutorialType ? ftuxSelectors.isTutorialDismissed(tutorialType) : () => false
	);

	// Handle manual restart trigger
	useEffect(() => {
		if (manualRestartTrigger !== undefined && manualRestartTrigger > 0 && tutorialType) {
			// Mark as manual restart to avoid API call on completion
			isManualRestartRef.current = true;
			// Temporarily reset tutorial in Redux (no API call) to allow restart
			dispatch(FtuxSlice.actions.resetTutorial(tutorialType));
			// Reset to first step and start running
			setCurrentStepIndex(0);
			setIsRunning(true);
		}
	}, [manualRestartTrigger, tutorialType, dispatch]);

	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [isRunning, setIsRunning] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [isWaitingForTarget, setIsWaitingForTarget] = useState(false);
	const [targetValidationError, setTargetValidationError] = useState<string | null>(null);

	const previousStepRef = useRef<{ step: FtuxStep | null; index: number }>({
		step: null,
		index: -1
	});
	const isManualRestartRef = useRef(false); // Track if this is a manual restart (to avoid API call on completion)

	const currentStep = config.steps[currentStepIndex];
	const shouldShowBackdrop = currentStep?.hasBackdrop ?? true;
	const isLastStep = currentStepIndex === config.steps.length - 1;

	const paginatedStepIndices = useMemo(() => {
		return config.steps.reduce<number[]>((accumulator, step, index) => {
			if (step.button?.type === 'paginated') {
				accumulator.push(index);
			}
			return accumulator;
		}, []);
	}, [config.steps]);

	const paginatedStepTotal = paginatedStepIndices.length > 0 ? paginatedStepIndices.length : null;
	const paginatedStepIndex = useMemo(() => {
		const indexWithinPaginated = paginatedStepIndices.indexOf(currentStepIndex);
		return indexWithinPaginated === -1 ? null : indexWithinPaginated + 1;
	}, [currentStepIndex, paginatedStepIndices]);

	const isFTUXPaused = useAppSelector(ftuxSelectors.isPaused);

	// Determine if tutorial should start
	// If tutorialType is provided, check dismissal status and onboarding completion
	// If no tutorialType, always start (for initial onboarding or tutorials without type)
	const shouldStart = useMemo(() => {
		if (tutorialType) {
			// For specific tutorials, only start if onboarding is completed and tutorial is not dismissed
			// Also check that Redux state has been initialized (onboardingCompleted is not undefined)
			return onboardingCompleted && !isTutorialDismissed;
		}
		// For initial onboarding or tutorials without type, always start
		return true;
	}, [tutorialType, onboardingCompleted, isTutorialDismissed]);

	// Initialize running state based on shouldStart
	useEffect(() => {
		setIsRunning(shouldStart);
	}, [shouldStart]);

	// Determine if current step is modal or tooltip
	const isModalStep = currentStep?.type === 'modal';
	const isTooltipStep = currentStep?.type === 'tooltip';

	// Prepare Joyride steps (only tooltip steps)
	const joyrideSteps: Step[] = config.steps
		.map((step, index) => {
			if (step.type === 'tooltip' && step.target) {
				if (step.requireTargetActive && !isTooltipTargetAvailable(step)) {
					return null;
				}
				return {
					target: step.target,
					content: step.subtitle || '',
					title: step.title,
					placement: step.tooltipPlacement || 'bottom',
					disableBeacon: step.disableBeacon ?? true,
					offset: 0,
					data: {
						...step.data,
						originalIndex: index,
						buttonLabel: step.button?.label
					}
				} as Step;
			}
			return null;
		})
		.filter((step): step is Step => step !== null);

	// Initialize FTUX in Redux when tutorial starts running
	useEffect(() => {
		if (isRunning && config.id) {
			dispatch(FtuxSlice.actions.begin({ ftuxId: config.id }));
		} else if (!isRunning && config.id) {
			// Clean up when tutorial stops running
			dispatch(FtuxSlice.actions.cancel());
		}

		return () => {
			// Clean up when component unmounts
			if (config.id) {
				dispatch(FtuxSlice.actions.cancel());
			}
		};
	}, [isRunning, config.id, dispatch]);

	// Update Redux with current target whenever step changes
	useEffect(() => {
		if (isRunning && currentStep) {
			// Extract target ID from the selector (e.g., '#weekly-plan-navigation' -> 'weekly-plan-navigation')
			const targetId = currentStep.target?.replace(/^#/, '') || null;
			dispatch(FtuxSlice.actions.setCurrentTarget(targetId));

			if (isModalStep) {
				setShowModal(true);
			} else {
				setShowModal(false);
			}
		}
	}, [currentStep, currentStepIndex, isRunning, isModalStep, dispatch]);

	useEffect(() => {
		if (!isRunning) {
			previousStepRef.current = { step: null, index: -1 };
			return;
		}

		const previous = previousStepRef.current;

		if (previous.step && currentStepIndex > previous.index && config.onStepNext) {
			config.onStepNext(previous.step, previous.index);
		}

		previousStepRef.current = { step: currentStep ?? null, index: currentStepIndex };
	}, [config, currentStep, currentStepIndex, isRunning]);

	const handleComplete = useCallback(async () => {
		setIsRunning(false);
		setShowModal(false);
		dispatch(FtuxSlice.actions.cancel());

		// If tutorialType is provided, dismiss it via API
		if (tutorialType && !isManualRestartRef.current) {
			try {
				const response = await apibridge.postTutorialDismiss({ type: tutorialType });
				if (response && response.data && !response.data.isError) {
					// Update Redux state to mark tutorial as dismissed
					dispatch(FtuxSlice.actions.dismissTutorial(tutorialType));
					dispatch(UserSlice.actions.dismissUserTutorial(tutorialType));
				}
			} catch (error) {
				console.error(`Error dismissing tutorial ${tutorialType}:`, error);
			}
		} else if (tutorialType && isManualRestartRef.current) {
			// Manual restart: Just mark as dismissed in Redux without API call
			dispatch(FtuxSlice.actions.dismissTutorial(tutorialType));
			dispatch(UserSlice.actions.dismissUserTutorial(tutorialType));
			isManualRestartRef.current = false; // Reset flag
		}

		if (config.onComplete) {
			config.onComplete();
		}
	}, [config, dispatch, tutorialType]);

	const handleNext = useCallback(() => {
		if (!currentStep) {
			return;
		}

		if (currentStep?.button?.link) {
			navigate(currentStep.button.link);
		}

		if (isLastStep) {
			if (config.onStepNext) {
				config.onStepNext(currentStep, currentStepIndex);
			}
			handleComplete();
		} else {
			setCurrentStepIndex((prev) => prev + 1);
			dispatch(FtuxSlice.actions.goNext());
		}
	}, [config, currentStep, currentStepIndex, dispatch, handleComplete, isLastStep, navigate]);

	const handleBack = useCallback(() => {
		setCurrentStepIndex((prev) => {
			if (prev > 0) {
				// Call onStepBack callback before going back
				if (config.onStepBack && currentStep) {
					config.onStepBack(currentStep, prev);
				}
				dispatch(FtuxSlice.actions.goBack());
				return prev - 1;
			}
			return prev;
		});
	}, [config, currentStep, dispatch]);

	const handleJoyrideCallback = (data: CallBackProps) => {
		const { action, status, type, step } = data;

		// Handle errors - if Joyride reports an error (e.g., target not found)
		if (status === STATUS.ERROR) {
			const targetString: string = typeof step?.target === 'string' ? step.target : 'Unknown target';
			console.error('[FTUX] Joyride error detected:', {
				step: targetString,
				action,
				type
			});
			setTargetValidationError(targetString);

			// Find the next available step starting from the next index (async to wait for dynamic content)
			findNextAvailableStepAsync(config.steps, currentStepIndex + 1, 500).then((nextAvailableIndex) => {
				if (nextAvailableIndex !== null) {
					// Found an available step, skip to it
					setCurrentStepIndex(nextAvailableIndex);
					dispatch(FtuxSlice.actions.goNext());
				} else {
					// No available steps found, complete the tutorial
					console.warn(`[FTUX] No available steps found after index ${currentStepIndex}. Completing tutorial.`);
					handleComplete();
				}
			});
			return;
		}

		if (status === STATUS.FINISHED) {
			handleComplete();
		} else if (type === EVENTS.STEP_AFTER) {
			if (action === ACTIONS.NEXT) {
				handleNext();
			}
		}
	};

	// Validate and wait for tooltip target before proceeding
	useEffect(() => {
		if (!isRunning || !isTooltipStep || !currentStep || currentStep.type !== 'tooltip') {
			setIsWaitingForTarget(false);
			setTargetValidationError(null);
			return;
		}

		// If no target is required, proceed normally
		if (!currentStep.target) {
			setIsWaitingForTarget(false);
			return;
		}

		// Check if target is immediately available
		if (isTooltipTargetAvailable(currentStep)) {
			setIsWaitingForTarget(false);
			setTargetValidationError(null);
			return;
		}

		let isCancelled = false;
		const currentTarget = currentStep.target;

		// If requireTargetActive is false, wait a short time for dynamic content
		// but will skip to next available step if it doesn't appear
		if (!currentStep.requireTargetActive) {
			setIsWaitingForTarget(true);

			// Wait up to 1 second for the element to appear (for dynamic content)
			waitForElement(currentTarget, 1000, 100).then((element) => {
				if (!isCancelled) {
					setIsWaitingForTarget(false);
					if (!element) {
						console.warn(`[FTUX] Target "${currentTarget}" not found. Looking for next available step.`);
						setTargetValidationError(currentTarget);

						// Find the next available step starting from the next index (async to wait for dynamic content)
						findNextAvailableStepAsync(config.steps, currentStepIndex + 1, 500).then((nextAvailableIndex) => {
							if (!isCancelled && nextAvailableIndex !== null) {
								// Found an available step, skip to it
								setCurrentStepIndex(nextAvailableIndex);
								dispatch(FtuxSlice.actions.goNext());
							} else if (!isCancelled) {
								// No available steps found, complete the tutorial
								console.warn(`[FTUX] No available steps found after index ${currentStepIndex}. Completing tutorial.`);
								handleComplete();
							}
						});
					}
				}
			});
			return () => {
				isCancelled = true;
			};
		}

		// If requireTargetActive is true, wait longer and skip if not found
		setIsWaitingForTarget(true);
		waitForElement(currentTarget, 5000, 100).then((element) => {
			if (!isCancelled) {
				setIsWaitingForTarget(false);
				if (!element) {
					setTargetValidationError(currentTarget);

					// Find the next available step starting from the next index (async to wait for dynamic content)
					findNextAvailableStepAsync(config.steps, currentStepIndex + 1, 500).then((nextAvailableIndex) => {
						if (!isCancelled && nextAvailableIndex !== null) {
							// Found an available step, skip to it
							setCurrentStepIndex(nextAvailableIndex);
							dispatch(FtuxSlice.actions.goNext());
						} else if (!isCancelled) {
							// No available steps found, complete the tutorial
							console.warn(`[FTUX] No available steps found after index ${currentStepIndex}. Completing tutorial.`);
							handleComplete();
						}
					});
				}
			}
		});

		return () => {
			isCancelled = true;
		};
	}, [config.steps, currentStep, currentStepIndex, dispatch, handleComplete, isLastStep, isRunning, isTooltipStep]);

	// Scroll to target element when tooltip step becomes active
	useEffect(() => {
		if (!isRunning || !isTooltipStep || !currentStep || currentStep.type !== 'tooltip' || !currentStep.target) {
			return;
		}

		// Wait a bit for the element to be rendered and positioned
		const scrollTimeout = setTimeout(() => {
			const targetElement = document.querySelector(currentStep.target!);
			if (targetElement) {
				targetElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'nearest'
				});
			}
		}, 100);

		return () => {
			clearTimeout(scrollTimeout);
		};
	}, [currentStepIndex, currentStep, isRunning, isTooltipStep]);

	// If we're on a modal step, show the modal
	if (isModalStep && isRunning) {
		return (
			<FtuxModal
				step={currentStep}
				currentStepIndex={currentStepIndex}
				totalSteps={config.steps.length}
				paginatedStepIndex={paginatedStepIndex}
				paginatedStepTotal={paginatedStepTotal}
				show={showModal}
				onNext={handleNext}
				onBack={handleBack}
				hasBackButton={currentStep.button?.hasBackButton ?? true}
				hasBackdrop={shouldShowBackdrop}
			/>
		);
	}

	// If we're on a tooltip step, use Joyride
	if (isTooltipStep && isRunning && joyrideSteps.length > 0) {
		// Find the joyride step index that matches our current step
		const joyrideStepIndex = joyrideSteps.findIndex((step) => step.data?.originalIndex === currentStepIndex);

		// Safety check: Don't render Joyride if:
		// 1. We're waiting for a target to appear
		// 2. There's a validation error for the current step
		// 3. The current step's target doesn't exist in the DOM
		// 4. The joyride step index is invalid
		if (
			isWaitingForTarget ||
			targetValidationError ||
			(joyrideStepIndex < 0 && currentStep?.target && !isTooltipTargetAvailable(currentStep))
		) {
			// Don't render anything while waiting or if there's an error
			// The useEffect above will handle skipping to the next step
			return null;
		}

		// Additional safety: Verify the target exists before rendering
		if (currentStep?.target && !isTooltipTargetAvailable(currentStep)) {
			console.warn(`[FTUX] Target "${currentStep.target}" not found in DOM. Skipping Joyride render.`);
			return null;
		}

		// Determine if current step should use flip behavior based on step configuration
		const shouldUseFlip = currentStep?.flip === true && currentStep?.tooltipPlacement === 'left';

		return (
			<Joyride
				key={currentStepIndex}
				steps={joyrideSteps}
				run={isRunning && !isFTUXPaused && !isWaitingForTarget}
				stepIndex={joyrideStepIndex >= 0 ? joyrideStepIndex : 0}
				continuous
				disableCloseOnEsc
				disableOverlayClose
				disableScrolling
				scrollToFirstStep
				disableScrollParentFix
				spotlightClicks={false}
				hideCloseButton={true}
				callback={handleJoyrideCallback}
				tooltipComponent={(props) => (
					<FtuxTooltip
						{...props}
						currentStepIndex={currentStepIndex}
						totalSteps={config.steps.length}
						paginatedStepIndex={paginatedStepIndex}
						paginatedStepTotal={paginatedStepTotal}
						onNext={handleNext}
						onBack={handleBack}
					/>
				)}
				floaterProps={{
					disableFlip: !shouldUseFlip,
					disableAnimation: true,
					...(shouldUseFlip && {
						options: {
							flip: {
								behavior: ['left', 'top'], // Try left first, then top if not enough space
								padding: 10
							},
							preventOverflow: {
								boundariesElement: typeof document !== 'undefined' ? document.documentElement : undefined,
								padding: 10
							}
						}
					})
				}}
				styles={{
					options: {
						arrowColor: '#E0F4FF',
						overlayColor: 'rgba(0, 0, 0, 0)', // transparent but still blocks clicks
						zIndex: 10001
					}
				}}
			/>
		);
	}

	return null;
};

export default DynamicFtux;
